/**
 * Hub data – the Application Hub directory lives in the application code itself:
 * src/data/app-directory.csv (the "App Directory" sheet exported as CSV).
 * It is bundled at build time, so there is no network call and no authentication.
 * To change the hub: edit that CSV, then build and deploy as usual.
 */
import appDirectoryCsv from '../data/app-directory.csv?raw'

/** Minimal RFC 4180 CSV parser: quoted fields, escaped quotes, newlines inside quotes. */
export function parseCsv(text) {
  const raw = String(text || '')
  const src = raw.charCodeAt(0) === 0xFEFF ? raw.slice(1) : raw
  const rows = []
  let row = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++ } else { inQuotes = false }
      } else {
        field += c
      }
    } else if (c === '"') {
      inQuotes = true
    } else if (c === ',') {
      row.push(field); field = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++
      row.push(field); rows.push(row); row = []; field = ''
    } else {
      field += c
    }
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row) }
  return rows
}

/** Sheet header (normalised) -> app field. Columns are matched by name, not position. */
const HEADER_MAP = {
  'app id':                   'id',
  'category':                 'cat',
  'application':              'name',
  "what it's used for":       'desc',
  'audience':                 'aud',
  'status':                   'status',
  'dev url':                  'dev',
  'uat url':                  'uat',
  'prod url':                 'prod',
  'ticket link':              'ticket',
  'business owner':           'owner',
  'technical owner / support':'techOwner',
  'search keywords':          'kw',
  'pin order':                'pin',
}

const normHeader = (h) => String(h || '').replace(/[‘’]/g, "'").trim().toLowerCase()
const clean      = (v) => { const s = String(v ?? '').trim(); return s === '' ? null : s }
/** Sheet content is editable by many people: only ever allow http(s) links. */
const safeUrl    = (v) => { const s = clean(v); return s && /^https?:\/\//i.test(s) ? s : null }

export function rowsToApps(rows) {
  const headerIdx = rows.findIndex((r) => {
    const h = r.map(normHeader)
    return h.includes('app id') && h.includes('application')
  })
  if (headerIdx === -1) {
    throw new Error('App Directory header row (App ID / Application) not found in the sheet')
  }

  const cols = rows[headerIdx].map((h) => HEADER_MAP[normHeader(h)] || null)
  const apps = []

  for (const r of rows.slice(headerIdx + 1)) {
    const rec = {}
    cols.forEach((key, i) => { if (key) rec[key] = r[i] })

    const id   = clean(rec.id)
    const name = clean(rec.name)
    if (!id || !name) continue // blank / formatting-only rows

    const pin = Number(clean(rec.pin))
    apps.push({
      id,
      cat:    clean(rec.cat) || 'Other',
      name,
      desc:   clean(rec.desc),
      aud:    clean(rec.aud) || 'All',
      status: clean(rec.status) || 'Active',
      dev:    safeUrl(rec.dev),
      uat:    safeUrl(rec.uat),
      prod:   safeUrl(rec.prod),
      kw:     clean(rec.kw) || '',
      pin:    clean(rec.pin) !== null && Number.isFinite(pin) ? pin : null,
      owner:  clean(rec.owner) || clean(rec.techOwner),
      ticket: safeUrl(rec.ticket),
    })
  }
  return apps
}

export function loadHubApps() {
  return rowsToApps(parseCsv(appDirectoryCsv))
}
